import { v4 as uuidv4 } from "uuid";
import {
  makeObservable,
  action,
  observable,
  makeAutoObservable,
  reaction,
} from "mobx";
import Serie from "./Serie";
import Attribute from "./Attribute";
import { ProtocolTitles, WorkoutLimits } from "../../Resources/Constants";
import i18n from "../../Localization/i18next";
import { validateExercise } from "../utils/firestoreValidation";
import { showDevToast } from "../utils/devToast";

class Exercise {
  updateSets = false;

  // secondProtocolExercise = null;
  // exercise = null;
  isSecondaryProtocol = false;

  protocolSlot = null;
  protocolText = "";
  protocolWeek = null;
  // protocolWeekDay = null;

  showActualWithDataWarning = false;
  ignoreActualWithDataWarning = false;

  isProtocolHovered = false;

  protocolSeries = [];

  constructor(
    id = uuidv4(),
    name = "",
    originalName = "",
    notes = "",
    order = 0,
    isShown = true,
    isSelected = false,
    workout = null,
    series = [],
    attributes = [],
    exerciseToAddId = "",
    block = null,
    link = "",
  ) {
    this.id = id;
    this.name = name;
    this.originalName = originalName;
    this.notes = notes;
    this.order = order;
    this.isShown = isShown;
    this.isSelected = isSelected;
    this.workout = workout;
    this.series = series;
    this.attributes = attributes;
    this.exerciseToAddId = exerciseToAddId;
    this.block = block;
    this.link = link;
    makeAutoObservable(this);

    reaction(
      () => ({
        id: this.id,
        name: this.name,
        original_name: this.originalName,
        notes: this.notes,
        link: this.link,
        series: this.series,
        // secondProtocolExercise: this.secondProtocolExercise,
        protocolSlot: this.protocolSlot,
        protocolText: this.protocolText,
      }),

      () => {
        if (this?.workout?.showingEditWorkout) {
          console.warn("early return reaction due to editing workout");
          return;
        }

        if (this.workout) {
          const { block, coach } = this.workout;

          if (block?.client) {
            const { client } = block;
            if (!client.avoidTransaction && client.isFetched) {
              this.workout.setProperty("hasChanged", true);
              client.updateClientTransactionData(block.id);
              client.startOrResetBatchTimer();
            }
          } else if (block?.coach && !block.coach.isFetching) {
            block.coach.startOrResetBatchTimer();
            block.coach.updateCoachTransactionBlocks(block.id.toLowerCase());
          } else if (coach) {
            this.workout.setProperty("hasChanged", true);
            coach.updateCoachTransactionWorkouts(this.workout.id);
            coach.startOrResetBatchTimer();
          }
        } else if (this.block) {
          if (
            this.block.client?.isFetched &&
            !this.block.client.avoidTransaction
          ) {
            this.block.client.updateClientTransactionData(this.block.id);
            this.block.client.startOrResetBatchTimer();
          } else if (this.block.coach) {
            this.block.coach.updateCoachTransactionBlocks(
              this.block.id.toLowerCase(),
            );
            this.block.coach.startOrResetBatchTimer();
          }
        }
      },
    );
  }

  // #region /* ADD EXERCISES */
  duplicateExercise() {
    const newExercise = new Exercise(
      uuidv4(),
      this.name,
      this.originalName,
      this.notes,
      this.order,
      true,
      false,
      this.workout,
      [],
      [],
      this.id.toString(),
      undefined,
      this.link,
    );

    newExercise.setProperty("protocolSlot", this.protocolSlot);
    newExercise.setProperty("protocolText", this.protocolText);
    newExercise.setProperty("isSecondaryProtocol", this.isSecondaryProtocol);

    this.series &&
      this.series.forEach((serie) => {
        const newSerie = new Serie(
          uuidv4(),
          serie.order,
          serie.reps,
          serie.rpe,
          serie.weight,
          serie.targetReps,
          serie.targetRpe,
          serie.targetWeight,
          newExercise,
          serie.relevance,
          undefined,
          serie.requireVideo,
        );
        newExercise.series.push(newSerie);
      });
    this.attributes &&
      this.attributes.forEach((attribute) => {
        const newAttribute = new Attribute(
          uuidv4(),
          attribute.name,
          attribute.title,
          attribute.subtitle,
          attribute.order,
          false,
          attribute.isSelected,
          null,
          newExercise,
        );
        newExercise.attributes.push(newAttribute);
      });

    this.workout.exercisesToAdd.push(newExercise);
  }

  duplicateExerciseToAdd() {
    if (this.exerciseToAddId) {
      let exerciseToChange = this.workout.exercises.find(
        (exercise) => exercise.id.toString() === this.exerciseToAddId,
      );

      if (exerciseToChange) {
        exerciseToChange.name = this.name;
        exerciseToChange.order = this.order;

        exerciseToChange.attributes = this.attributes;
      } else {
        const newExercise = new Exercise(
          uuidv4(),
          this.name,
          this.originalName,
          this.notes,
          this.order,
          true,
          false,
          this.workout,
          [],
          [],
          undefined,
          undefined,
          this.link,
        );

        newExercise.setProperty("protocolSlot", this.protocolSlot);
        newExercise.setProperty("protocolText", this.protocolText);
        newExercise.setProperty(
          "isSecondaryProtocol",
          this.isSecondaryProtocol,
        );

        this.series &&
          this.series.forEach((serie) => {
            const newSerie = new Serie(
              uuidv4(),
              serie.order,
              serie.reps,
              serie.rpe,
              serie.weight,
              serie.targetReps,
              serie.targetRpe,
              serie.targetWeight,
              newExercise,
              serie.relevance,
              undefined,
              serie.requireVideo,
            );
            newExercise.series.push(newSerie);
          });
        this.attributes &&
          this.attributes.forEach((attribute) => {
            const newAttribute = new Attribute(
              uuidv4(),
              attribute.name,
              attribute.title,
              attribute.subtitle,
              attribute.order,
              false,
              attribute.isSelected,
              null,
              newExercise,
            );
            newExercise.attributes.push(newAttribute);
          });

        if (this.workout) {
          this.workout.exercises.push(newExercise);
        } else {
          console.log("NO WORKOUT");
        }
      }
    }
    this.exerciseToAddId = "";
  }

  duplicateSlotExercise(workout) {
    const maxOrder = workout.exercisesToAdd.length //Revisar el per que no fa > 0
      ? Math.max(...workout.exercisesToAdd.map((ex) => ex.order))
      : -1;
    const newOrder = maxOrder + 1;

    // const newId = uuidv4();
    const newExercise = new Exercise(
      undefined,
      this.name,
      this.originalName,
      this.notes,
      newOrder,
      true,
      false,
      workout,
      [],
      [],
      uuidv4(),
      undefined,
      this.link,
    );

    newExercise.protocolSlot = this.protocolSlot;
    newExercise.protocolText = this.protocolText;

    this.series?.forEach((serie) => {
      newExercise.copySerie(serie);
    });
    this.attributes?.forEach((attribute) => {
      newExercise.copyAttribute(attribute);
    });

    workout.exercisesToAdd.push(newExercise);

    if (this.secondProtocolExercise) {
      const secondExercise = new Exercise(
        undefined,
        this.secondProtocolExercise.name,
        this.secondProtocolExercise.originalName,
        this.secondProtocolExercise.notes,
        newOrder + 1,
        true,
        false,
        workout,
        [],
        [],
        uuidv4(),
        undefined,
        this.secondProtocolExercise.link,
      );

      secondExercise.setProperty("protocolSlot", this.protocolSlot);
      secondExercise.setProperty(
        "protocolText",
        this.secondProtocolExercise.protocolText,
      );
      secondExercise.setProperty("isSecondaryProtocol", true);
      this.secondProtocolExercise.series?.forEach((serie) => {
        secondExercise.copySerie(serie);
      });
      this.secondProtocolExercise.attributes?.forEach((attribute) => {
        secondExercise.copyAttribute(attribute);
      });

      workout.exercisesToAdd.push(secondExercise);
    }
  }
  // #endregion

  // #region /* BLOCK PROTOCOLS */
  get secondProtocolExercise() {
    const block = this.block;
    if (!block) {
      console.error(
        "calling .secondProtocolExercise in a exercise from workout",
      );
      return null;
    }
    // Look for a protocol exercise in the block that matches this slot & week AND is marked secondary
    const found = block.protocols.find(
      (ex) =>
        ex.protocolWeek === this.getWeek &&
        ex.protocolSlot === this.protocolSlot &&
        ex.isSecondaryProtocol,
    );
    return found ?? null;
  }

  get exercise() {
    const block = this.block;
    if (!block) {
      console.error("calling .exercise in a exercise from workout");
      return null;
    }

    // Look for a protocol exercise in the block that matches this slot & week AND is marked secondary
    const found = block.protocols.find(
      (ex) =>
        ex.protocolWeek === this.getWeek &&
        ex.protocolSlot === this.protocolSlot &&
        !ex.isSecondaryProtocol,
    );
    return found ?? null;
  }

  get getDate() {
    if (this.workout && this.workout.date) {
      return this.workout.date;
    } else {
      return null;
    }
  }

  get getWeek() {
    if (this.workout && this.workout.week) {
      return this.workout.week;
    } else {
      return this.protocolWeek;
    }
  }

  get getBlockDashboardShowingActual() {
    if (this.block) {
      return this.block.showingDashboardActual;
    } else if (this.workout.block) {
      return this.workout.block.showingDashboardActual;
    } else {
      return false;
    }
  }

  get getProtocolBasicExercise() {
    if (
      this.protocolSlot === ProtocolTitles.PRIMARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.SECONDARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.TERTIARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.QUATERNARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.QUINARY_SQUAT
    ) {
      return "squat";
    } else if (
      this.protocolSlot === ProtocolTitles.PRIMARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.SECONDARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.TERTIARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.QUATERNARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.QUINARY_BENCH_PRESS
    ) {
      return "benchPress";
    } else if (
      this.protocolSlot === ProtocolTitles.PRIMARY_DEADLIFT ||
      this.protocolSlot === ProtocolTitles.SECONDARY_DEADLIFT ||
      this.protocolSlot === ProtocolTitles.TERTIARY_DEADLIFT ||
      this.protocolSlot === ProtocolTitles.QUATERNARY_DEADLIFT ||
      this.protocolSlot === ProtocolTitles.QUINARY_DEADLIFT
    ) {
      return "deadlift";
    } else {
      return "";
    }
  }

  get getProtocolPriority() {
    if (
      this.protocolSlot === ProtocolTitles.PRIMARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.PRIMARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.PRIMARY_DEADLIFT
    ) {
      return "primary";
    } else if (
      this.protocolSlot === ProtocolTitles.SECONDARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.SECONDARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.SECONDARY_DEADLIFT
    ) {
      return "secondary";
    } else if (
      this.protocolSlot === ProtocolTitles.TERTIARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.TERTIARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.TERTIARY_DEADLIFT
    ) {
      return "tertiary";
    } else if (
      this.protocolSlot === ProtocolTitles.QUATERNARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.QUATERNARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.QUATERNARY_DEADLIFT
    ) {
      return "quaternary";
    } else if (
      this.protocolSlot === ProtocolTitles.QUINARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.QUINARY_BENCH_PRESS ||
      this.protocolSlot === ProtocolTitles.QUINARY_DEADLIFT
    ) {
      return "quaternary";
    } else {
      return "";
    }
  }

  createSecondProtocol() {
    const originalName =
      this.protocolSlot === ProtocolTitles.PRIMARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.SECONDARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.TERTIARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.QUATERNARY_SQUAT ||
      this.protocolSlot === ProtocolTitles.QUINARY_SQUAT
        ? "squat-string"
        : this.protocolSlot === ProtocolTitles.PRIMARY_BENCH_PRESS ||
            this.protocolSlot === ProtocolTitles.SECONDARY_BENCH_PRESS ||
            this.protocolSlot === ProtocolTitles.TERTIARY_BENCH_PRESS ||
            this.protocolSlot === ProtocolTitles.QUATERNARY_BENCH_PRESS ||
            this.protocolSlot === ProtocolTitles.QUINARY_BENCH_PRESS
          ? "benchPress-string"
          : "sumoDeadlift-string";

    const newExercise = new Exercise(
      undefined,
      originalName,
      originalName,
      undefined,
      this.order + 1,
      undefined,
      true,
      null,
      [],
      [],
      undefined,
    );

    newExercise.protocolSlot = this.protocolSlot;
    newExercise.protocolWeek = this.protocolWeek;
    newExercise.isSecondaryProtocol = true;

    for (let i = 0; i < 3; i++) {
      const newSerie = new Serie(
        uuidv4(),
        i,
        0,
        0,
        0,
        undefined,
        undefined,
        undefined,
        newExercise,
        undefined,
      );
      newExercise.series.push(newSerie);
    }

    if (this.workout) {
      newExercise.workout = this.workout;
      this.workout.hasChanged = true;

      this.workout.exercises.forEach((ex) => {
        if (ex.order > this.order) {
          ex.order += 1;
        }
      });

      this.workout.exercises.push(newExercise);

      this.workout.block.setProperty(
        "selectedSlotId",
        newExercise.id.toLowerCase(),
      );
      this.workout.block.setProperty("showingInfoView", true);
    } else if (this.block) {
      newExercise.setProperty("block", this.block);
      this.block.protocols.push(newExercise);
      this.block.setProperty("selectedSlotId", newExercise.id.toLowerCase());
      this.block.setProperty("showingInfoView", true);
    }
    this.markChanged();
  }

  /**
   * Add a second protocol exercise from search result
   * @param {Object} searchResult - Result object from ExerciseSearchFloating
   * @param {Function} t - Translation function
   * @param {Object} coach - Coach object for applying modifiers
   */
  addSecondProtocolFromSearchResult(searchResult, t, coach) {
    if (!searchResult) return null;

    const newExercise = new Exercise(
      undefined,
      searchResult.exerciseName,
      searchResult.exerciseOriginalName,
      undefined,
      this.order + 1,
      undefined,
      true,
      null,
      [],
      [],
      undefined,
    );

    newExercise.protocolSlot = this.protocolSlot;
    newExercise.protocolWeek = this.protocolWeek;
    newExercise.isSecondaryProtocol = true;

    // Add default series
    for (let i = 0; i < 3; i++) {
      const newSerie = new Serie(
        uuidv4(),
        i,
        0,
        0,
        0,
        undefined,
        undefined,
        undefined,
        newExercise,
        undefined,
      );
      newExercise.series.push(newSerie);
    }

    // Apply modifiers (attributes) if any
    if (searchResult.modifiers && searchResult.modifiers.length > 0 && coach) {
      searchResult.modifiers.forEach((modifier) => {
        const attribute = coach.customPredefinedAttributes.find(
          (attr) =>
            attr.id === modifier.id || attr.name === modifier.originalName,
        );

        if (attribute) {
          coach.selectAttribute(newExercise, attribute, t, false);
        }
      });
    }

    // //Get the exercise name and set it to exercise.name
    // newExercise.setProperty("name", newExercise.getName(t, i18n.language));

    // Add to workout or block
    if (this.workout) {
      newExercise.workout = this.workout;
      this.workout.hasChanged = true;

      this.workout.exercises.forEach((ex) => {
        if (ex.order > this.order) {
          ex.order += 1;
        }
      });

      this.workout.exercises.push(newExercise);

      this.workout.block.setProperty(
        "selectedSlotId",
        newExercise.id.toLowerCase(),
      );
    } else if (this.block) {
      newExercise.setProperty("block", this.block);
      this.block.protocols.push(newExercise);
      this.block.setProperty("selectedSlotId", newExercise.id.toLowerCase());
    }

    this.markChanged();
    return newExercise;
  }

  get getSecondProtocol() {
    if (this.workout) {
      if (!this.workout.exercises) {
        console.error("no exercises for this workout", this.workout);
        return null;
      }

      return (
        this.workout.exercises.find(
          (ex) =>
            ex.protocolSlot === this.protocolSlot && ex.isSecondaryProtocol,
        ) ?? null
      );
    }
    return this.secondProtocolExercise ?? null;
  }

  copyExerciseToBlockProtocols() {
    const block = this?.workout?.block;
    if (!block) {
      console.log("copyExerciseToBlockProtocols: no block", {
        exerciseId: this.id,
        protocolSlot: this.protocolSlot,
      });
      return null;
    }

    if (!this.isSecondaryProtocol) {
      const newSlot = block.createSlotFromWorkoutExercise(this, false);
      if (!newSlot) {
        console.log(
          "copyExerciseToBlockProtocols: primary slot creation failed",
          {
            exerciseId: this.id,
            protocolSlot: this.protocolSlot,
          },
        );
        return null;
      }
      return newSlot;
    }

    const newSecondSlot = block.createSlotFromWorkoutExercise(this, true);
    if (newSecondSlot) {
      return newSecondSlot;
    }

    const primaryExercise = this.workout.exercises.find(
      (e) => e.protocolSlot === this.protocolSlot && !e.isSecondaryProtocol,
    );
    if (primaryExercise) {
      block.createSlotFromWorkoutExercise(primaryExercise, false);
      const retrySecond = block.createSlotFromWorkoutExercise(this, true);
      return retrySecond;
    }

    console.log(
      "copyExerciseToBlockProtocols: could not create secondary slot",
      {
        exerciseId: this.id,
        protocolSlot: this.protocolSlot,
      },
    );
    return null;
  }
  // #endregion

  // #region /* STATS */
  get getRirOrRpe() {
    if (this.workout) {
      if (this.workout.block) {
        if (this.workout.block.coach) {
          return this.workout.block.coach.rirOrRpe;
        } else if (this.workout.block.client.coach) {
          return this.workout.block.client.coach.rirOrRpe;
        }
      } else if (this.workout.coach) {
        return this.workout.coach.rirOrRpe;
      }
    } else if (this.block) {
      if (this.block.client) {
        return this.block.client.coach.rirOrRpe;
      } else if (this.block.coach) {
        return this.block.coach.rirOrRpe;
      }
    }
    return false; // Default value if no valid case is found
  }

  get getExerciseUnit() {
    if (this.workout) {
      if (this.workout.block) {
        if (this.workout.block.coach) {
          return this.workout.block.coach.exerciseUnit;
        } else if (this.workout.block.client) {
          return this.workout.block.client.exerciseUnit;
        }
      } else if (this.workout.coach) {
        return this.workout.coach.exerciseUnit;
      }
    } else if (this.block) {
      if (this.block.client) {
        return this.block.client.coach.exerciseUnit;
      } else if (this.block.coach) {
        return this.block.coach.exerciseUnit;
      }
    }
    return false; // Default return if no valid case is found
  }

  get getExerciseUnitString() {
    let bool = false;
    if (this.workout) {
      if (this.workout.block) {
        if (this.workout.block.coach) {
          bool = this.workout.block.coach.exerciseUnit;
        } else if (this.workout.block.client) {
          bool = this.workout.block.client.exerciseUnit;
        }
      } else if (this.workout.coach) {
        bool = this.workout.coach.exerciseUnit;
      }
    } else if (this.block) {
      if (this.block.client) {
        bool = this.block.client.coach.exerciseUnit;
      } else if (this.block.coach) {
        bool = this.block.coach.exerciseUnit;
      }
    }
    return bool ? "lbs" : "kg";
  }

  get getTonnage() {
    let totalTonnage = 0;
    for (const serie of this.series) {
      const tonnage = serie.weight * serie.reps;
      totalTonnage += tonnage;
      if (totalTonnage >= 99999) {
        return 99999;
      }
    }
    return totalTonnage;
  }

  get getMaxErm() {
    let maxErm = 0;

    for (const serie of this.series) {
      let eReps;
      if (this.getRirOrRpe) {
        // RIR
        eReps = serie.reps + serie.rpe;
      } else {
        // RPE
        eReps = serie.reps + (10.0 - serie.rpe);
      }
      const serieErm =
        serie.reps >= 1
          ? ((serie.weight * 100) / (48.8 + 53.8 * Math.exp(-0.06 * eReps))) *
            0.99466
          : 0;

      if (maxErm < serieErm) {
        maxErm = serieErm;
        if (maxErm >= 99999) {
          return 99999;
        }
      }
    }
    return maxErm;
  }

  get getStressIndex() {
    const rpeToStressIndex = {
      4: 0.2,
      5: 0.4,
      6: 0.6,
      7: 0.8,
      8: 1,
      9: 1.2,
      10: 1.6,
    };

    let stressIndex = 0;
    this.series.forEach((serie) => {
      const floorRPE = Math.floor(serie.rpe);
      const ceilRPE = Math.ceil(serie.rpe);

      // For whole RPE values, use the mapping directly.
      if (floorRPE === serie.rpe) {
        stressIndex += rpeToStressIndex[serie.rpe] || 0;
      } else {
        // Interpolate stress index for fractional RPE values.
        const floorStressIndex = rpeToStressIndex[floorRPE] || 0;
        const ceilStressIndex = rpeToStressIndex[ceilRPE] || 0;
        const fraction = serie.rpe - floorRPE;
        stressIndex +=
          floorStressIndex + (ceilStressIndex - floorStressIndex) * fraction;
      }
    });
    return stressIndex;
  }

  // Function to adjust intensity based on RPE
  adjustIntensityForRPE(rpe) {
    // These adjustments are derived based on the RPE table you provided
    const rpeAdjustments = {
      10: 1,
      9.5: 0.975,
      9: 0.95,
      8.5: 0.925,
      8: 0.9,
      7.5: 0.875,
      7: 0.85,
      6.5: 0.825,
      6: 0.8,
      5.5: 0.775,
      5: 0.75,
      4.5: 0.725,
      4: 0.7,
      3.5: 0.675,
      3: 0.65,
    };

    return rpeAdjustments[rpe] || 1; // Default to 1 if RPE is not in the table
  }

  get getAverageIntensity() {
    let totalAverageIntensity = 0;

    // If *all* sets are placeholders (no logged data), treat the exercise as
    // having no intensity rather than defaulting to 100%.
    const hasAnyLoggedData = this.series.some((serie) => {
      const reps = Number(serie?.reps ?? 0) || 0;
      const rpeOrRir = Number(serie?.rpe ?? 0) || 0;
      const weight = Number(serie?.weight ?? 0) || 0;
      return !(reps === 0 && rpeOrRir === 0 && weight === 0);
    });

    if (!hasAnyLoggedData) {
      return 0;
    }

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    for (const serie of this.series) {
      const reps = Number(serie?.reps ?? 0) || 0;
      const rpeOrRir = Number(serie?.rpe ?? 0) || 0;
      const weight = Number(serie?.weight ?? 0) || 0;

      // Placeholder/empty sets (no logged data) should not yield >100% due to
      // formula extrapolation.
      if (reps === 0 && rpeOrRir === 0 && weight === 0) {
        totalAverageIntensity += 100;
        continue;
      }

      let eReps;
      if (this.getRirOrRpe) {
        // RIR
        eReps = reps + rpeOrRir;
      } else {
        // RPE
        eReps = reps + (10.0 - rpeOrRir);
      }

      const numerator = 48.8 + 53.8 * Math.exp(-0.06 * eReps);
      const intensitySerieRaw = numerator / 0.99466;

      // Intensity percentage is defined as %1RM, so it cannot exceed 100.
      // The underlying formula can exceed 100 for very small eReps (e.g. 0).
      const intensitySerie = clamp(intensitySerieRaw, 0, 100);
      totalAverageIntensity += intensitySerie;

      // Early termination if average would exceed 99999
      if (totalAverageIntensity / this.series.length >= 99999) {
        return 99999;
      }
    }

    return this.series.length > 0
      ? Math.min(totalAverageIntensity / this.series.length, 99999)
      : 0;
  }

  get getNumberLifts() {
    let totalLifts = 0;

    this.series.forEach((serie) => {
      totalLifts += serie.reps;
    });

    return totalLifts;
  }

  get getNumberSeries() {
    return this.series.length;
  }

  get getExertionLoad() {
    let totalExertionLoad = 0;

    for (const serie of this.series) {
      // Early safety checks for extreme values to prevent crash due to CPU overload
      if (serie.weight > 10000 || serie.reps > 1000) {
        return 99999;
      }

      // Clamp RPE between 0 and 10
      const clampedRpe = Math.max(0, Math.min(10, serie.rpe));
      const rir = 10 - clampedRpe;

      // Limit reps to prevent excessive computation
      const maxReps = Math.min(serie.reps, 100);
      let setExertionLoad = 0;

      for (let i = 1; i <= maxReps; i++) {
        const exponent = -0.215 * (rir + maxReps - i);

        // Prevent extreme calculations and early termination
        if (exponent > 100) {
          return 99999;
        }

        const expValue = Math.exp(exponent);

        if (!isFinite(expValue) || expValue > 1000000) {
          return 99999;
        }

        setExertionLoad += expValue;

        // Early termination if set load becomes too high
        if (setExertionLoad > 1000) {
          return 99999;
        }
      }

      totalExertionLoad += serie.weight * setExertionLoad;

      // Early termination if total exceeds limit
      if (totalExertionLoad >= 99999) {
        return 99999;
      }
    }

    return totalExertionLoad;
  }

  getAverageRpe() {
    // Filter out series with rpe equal to 0
    const filteredSeries = this.series.filter((serie) => serie.rpe !== 0);

    // Return 0 if there are no series with rpe != 0 to avoid division by zero
    if (filteredSeries.length === 0) {
      return 0;
    }

    // Calculate the sum of rpe values
    const totalRpe = filteredSeries.reduce((sum, serie) => sum + serie.rpe, 0);

    // Calculate the average RPE
    const averageRpe = totalRpe / filteredSeries.length;

    // Ensure the result is a number and not NaN; if NaN, return 0
    return isNaN(averageRpe) ? 0 : averageRpe;
  }
  // #endregion

  // #region /* ATTRIBUTE ACTIONS */
  setAttributes(newArray) {
    this.attributes = newArray;
  }

  deleteAttribute(attribute, id) {
    if (id === true) {
      this.attributes = this.attributes.filter(
        (att) => att.name !== attribute.name,
      );
    } else {
      this.attributes = this.attributes.filter(
        (att) => att.title !== attribute.title,
      );
    }
  }
  // #endregion

  // #region /* SERIE ACTIONS */
  duplicateSerieBelow(serie) {
    const sortedSeries = this.series.slice().sort((a, b) => a.order - b.order);

    const currentIndex = sortedSeries.findIndex((s) => s.id === serie.id);

    if (currentIndex === -1 || currentIndex === sortedSeries.length - 1) {
      return;
    }

    for (let i = currentIndex + 1; i < sortedSeries.length; i++) {
      if (!this.areSeriesEqual(sortedSeries[currentIndex], sortedSeries[i])) {
        // Duplicate each relevant property individually
        this.copySerieProperties(sortedSeries[currentIndex], sortedSeries[i]);
        // this.workout.block.toggleHasChanged();
        break;
      }
    }
    this.updateSets = !this.updateSets;
  }

  copySerieProperties(sourceSerie, targetSerie) {
    targetSerie.targetWeight = sourceSerie.targetWeight;
    targetSerie.targetReps = sourceSerie.targetReps;
    targetSerie.targetRpe = sourceSerie.targetRpe;
    targetSerie.relevance = sourceSerie.relevance;
    targetSerie.requireVideo = sourceSerie.requireVideo;
  }

  areSeriesEqual(serie1, serie2) {
    return (
      serie1.targetWeight === serie2.targetWeight &&
      serie1.targetReps === serie2.targetReps &&
      serie1.targetRpe === serie2.targetRpe &&
      serie1.relevance === serie2.relevance &&
      serie1.requireVideo === serie2.requireVideo
    );
  }

  get getSortedSeries() {
    return this.series.slice().sort((a, b) => a.order - b.order);
  }

  deleteLastSerie() {
    if (this.series.length > 0) {
      const sortedSeries = this.series
        .slice()
        .sort((a, b) => a.order - b.order);

      const lastSerie = sortedSeries[sortedSeries.length - 1];

      const index = this.series.findIndex((serie) => serie.id === lastSerie.id);

      if (index > -1) {
        this.series.splice(index, 1);
        if (this.workout) {
          this.workout.setProperty("hasChanged", true);
        }

        this.markChanged();
      }
    }
  }

  deleteSerie(serie) {
    if (this.series.length <= 1) return;
    const index = this.series.findIndex((s) => s.id === serie.id);
    if (index === -1) return;

    this.series.splice(index, 1);
    this.series.slice(index).forEach((s) => s.order--);

    this.markChanged();
  }

  moveSerie(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= this.series.length || toIndex >= this.series.length)
      return;

    // Sort by order first to ensure correct indices
    const sortedSeries = this.series.slice().sort((a, b) => a.order - b.order);

    // Remove the item from its original position
    const [movedItem] = sortedSeries.splice(fromIndex, 1);

    // Insert at the new position
    sortedSeries.splice(toIndex, 0, movedItem);

    // Update order property for all items
    sortedSeries.forEach((serie, idx) => {
      serie.order = idx;
    });

    // Update the array reference to trigger MobX reactivity
    this.series = sortedSeries;

    this.markChanged();
  }

  addSerie() {
    // Do not add more than MAX_SETS_PER_EXERCISE sets per exercise
    // Note: UI layer (ExerciseCard) also shows toast when limit is reached
    if (this.series.length >= WorkoutLimits.MAX_SETS_PER_EXERCISE) {
      return;
    }
    const newOrder =
      this.series.length > 0
        ? Math.max(...this.series.map((s) => s.order)) + 1
        : 0;
    const newSerie = new Serie(
      undefined,
      newOrder,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this,
      3,
    );
    this.series.push(newSerie);

    this.markChanged();
  }

  pasteCopiedSets(copiedSets) {
    if (!copiedSets) return;

    if (typeof copiedSets?.protocolText === "string") {
      this?.setProperty?.("protocolText", copiedSets.protocolText);
    }

    const srcSeries = Array.isArray(copiedSets?.sets) ? copiedSets.sets : [];
    if (srcSeries.length === 0) return;

    const sortedExisting = (
      Array.isArray(this?.series) ? this.series.slice() : []
    )
      .filter(Boolean)
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

    // Normalize ordering first (so deletes happen from the end).
    this?.setProperty?.("series", sortedExisting);

    // Remove extra sets.
    while (
      Array.isArray(this.series) &&
      this.series.length > srcSeries.length
    ) {
      this.series.splice(this.series.length - 1, 1);
    }

    // Create missing sets.
    while (
      Array.isArray(this.series) &&
      this.series.length < srcSeries.length
    ) {
      const newOrder = this.series.length;
      const newSerie = new Serie(
        undefined,
        newOrder,
        0,
        0,
        0,
        "",
        "",
        "",
        this,
        3,
      );
      this.series.push(newSerie);
    }

    // Apply targets (and keep order consistent) without overwriting actuals.
    srcSeries.forEach((src, idx) => {
      const target = Array.isArray(this.series) ? this.series[idx] : null;
      if (!target?.setProperty) return;
      target.setProperty("order", idx);
      target.setProperty("targetWeight", src?.targetWeight ?? "");
      target.setProperty("targetReps", src?.targetReps ?? "");
      target.setProperty("targetRpe", src?.targetRpe ?? "");
      if (typeof src?.relevance === "number") {
        target.setProperty("relevance", src.relevance);
      }
      if (typeof src?.requireVideo === "boolean") {
        target.setProperty("requireVideo", src.requireVideo);
      }
    });
  }

  markChanged() {
    if (this.workout) {
      this.workout.setProperty("hasChanged", true);
      if (this.workout.block?.client) {
        //Client
        this.workout.block.client.updateClientTransactionData(
          this.workout.block.id,
        );
        this.workout.block.client.startOrResetBatchTimer();
      } else if (this.workout.block?.coach) {
        //Coach Block Library
        this.workout.block.coach.updateCoachTransactionBlocks(
          this.workout.block.id,
        );
        this.workout.block.coach.startOrResetBatchTimer();
      } else if (this.workout.coach) {
        //Coach Workout Library
        this.workout.coach.updateCoachTransactionWorkouts(this.workout.id);
        this.workout.coach.startOrResetBatchTimer();
      }
    } else if (this.block) {
      if (this.block.client) {
        //Client Block Protocols
        this.block.client.updateClientTransactionData(this.block.id);
        this.block.client.startOrResetBatchTimer();
      } else if (this.block.coach) {
        //Coach Block Library Protocols
        this.block.coach.updateCoachTransactionBlocks(this.block.id);
        this.block.coach.startOrResetBatchTimer();
      }
    }
  }
  // #endregion

  // #region /* REAL-TIME & COPY */
  fetchSerie(serie) {
    const newSerie = new Serie(
      String(serie.id),
      serie.order,
      serie.reps,
      serie.rpe,
      serie.weight,
      serie.target_reps,
      serie.target_rpe,
      serie.target_weight,
      this,
      serie.relevance,
      serie.has_video,
      serie.require_video,
    );

    this.series.push(newSerie);
  }
  fetchAttribute(attribute) {
    const newAttribute = new Attribute(
      String(attribute.id),
      attribute.name,
      attribute.title,
      attribute.subtitle,
      attribute.order,
      attribute.is_predefined,
      false,
      null,
      this,
    );
    this.attributes.push(newAttribute);
  }
  updateExercise(exercise) {
    this.updateSets = !this.updateSets;

    this.name = exercise.name ?? "";
    this.order = exercise.order ?? 0;
    this.originalName = exercise.original_name ?? "";
    this.notes = exercise.notes ?? "";
    this.link = exercise.link ?? null;

    this.isSecondaryProtocol = exercise.is_secondary_protocol ?? false;
    this.protocolSlot = exercise.protocol_slot ?? null;
    this.protocolWeek = exercise.protocol_week ?? null;
    this.protocolText = exercise.protocol_text ?? "";
  }

  uploadToCloud() {
    let seriesArray = this.series.map((serie) => serie.uploadToCloud());

    let attributesArray = this.attributes.map((attribute) =>
      attribute.uploadToCloud(),
    );

    const data = {
      id: this.id.toString().toLowerCase(),
      name: this.name,
      notes: this.notes,
      order: this.order,
      original_name: this.originalName,
      series: seriesArray,
      attributes: attributesArray,
      ...(this.isSecondaryProtocol
        ? { is_secondary_protocol: this.isSecondaryProtocol }
        : {}),
      ...(this.protocolSlot ? { protocol_slot: this.protocolSlot } : {}),
      ...(this.protocolText && this.protocolText != ""
        ? { protocol_text: this.protocolText }
        : {}),
      ...(this.protocolWeek && this.block
        ? { protocol_week: this.protocolWeek }
        : {}),
      ...(this.link && this.link != "" ? { link: this.link } : {}),
      // ...(this.secondProtocolExercise
      //   ? {
      //       second_protocol_exercise:
      //         this.secondProtocolExercise.uploadToCloud(),
      //     }
      //   : {}),
    };

    // Validate data (always runs, lightweight)
    // Only show toast in development
    if (process.env.NODE_ENV === "development") {
      const validation = validateExercise(data);
      if (!validation.valid) {
        const errorMsg = validation.errors.join("; ");
        showDevToast("Exercise validation failed", errorMsg);
      }
    }

    return data;
  }

  copySerie(serie, keepActual = false, mode = "target") {
    const newSerie = new Serie(
      undefined,
      serie.order,
      keepActual && mode != "targetToActual" ? serie.reps : undefined,
      keepActual && mode != "targetToActual" ? serie.rpe : undefined,
      keepActual && mode != "targetToActual" ? serie.weight : undefined,
      mode === "targetToActual" ? serie.reps : serie.targetReps,
      mode === "targetToActual" ? serie.rpe : serie.targetRpe,
      mode === "targetToActual" ? serie.weight : serie.targetWeight,
      this,
      serie.relevance,
      undefined,
      serie.requireVideo,
    );

    this.series.push(newSerie);
  }

  copyAttribute(attribute) {
    const newAttribute = new Attribute(
      undefined,
      attribute.name,
      attribute.title,
      attribute.subtitle,
      attribute.order,
      attribute.isPredefined,
      false,
      null,
      this,
    );
    this.attributes.push(newAttribute);
  }

  // fetchSlotExercise(exercise) {
  //   const newExercise = new Exercise(
  //     String(exercise.id),
  //     exercise.name,
  //     exercise.original_name,
  //     exercise.notes,
  //     exercise.order,
  //     undefined,
  //     false,
  //     this,
  //     [],
  //     []
  //   );

  //   newExercise.isSecondaryProtocol = true;

  //   newExercise.protocolSlot = exercise.protocol_slot ?? null;
  //   newExercise.protocolWeek = exercise.protocol_week ?? null;
  //   newExercise.protocolText = exercise.protocol_text ?? "";

  //   exercise?.series.forEach((serie) => {
  //     newExercise.fetchSerie(serie);
  //   });
  //   exercise?.attributes.forEach((attribute) => {
  //     newExercise.fetchAttribute(attribute);
  //   });
  //   // this.secondProtocolExercise = newExercise;
  //   // newExercise.exercise = this;
  // }
  // #endregion

  // #region /* HELPERS */
  getName(t, language) {
    let selectedEquipment = "";
    let selectedTempo = "";
    let selectedROM = "";
    let selectedStanceGrip = "";
    let selectedLoadAccomodation = "";
    let selectedAccessories = "";
    let selectedOther = "";
    if (language === "es") {
      this.attributes &&
        this.attributes.forEach((attribute) => {
          const attributeName = t(attribute.name);
          switch (attribute.title) {
            case Attribute.AttributeTitles.equipment:
              selectedEquipment = ` con ${attributeName}`;
              break;
            case Attribute.AttributeTitles.rom:
              selectedROM = ` ${attributeName}`;
              break;
            case Attribute.AttributeTitles.tempo:
              selectedTempo = ` ${attributeName}`;
              break;
            case Attribute.AttributeTitles.stanceGrip:
              selectedStanceGrip = ` ${attributeName}`;
              break;
            case Attribute.AttributeTitles.loadAccomodation:
              selectedLoadAccomodation += ` ${attributeName}`;
              break;
            case Attribute.AttributeTitles.accessories:
              selectedAccessories += ` ${attributeName}`;
              break;
            default:
              selectedOther += ` ${attributeName}`;
          }
        });

      return `${t(
        this.originalName,
      )}${selectedTempo}${selectedROM}${selectedStanceGrip}${selectedLoadAccomodation}${selectedAccessories}${selectedOther}${selectedEquipment}`;
    } else {
      this.attributes &&
        this.attributes.forEach((attribute) => {
          const attributeName = t(attribute.name);
          switch (attribute.title) {
            case Attribute.AttributeTitles.equipment:
              selectedEquipment = `${attributeName} `;
              break;
            case Attribute.AttributeTitles.rom:
              selectedROM = `${attributeName} `;
              break;
            case Attribute.AttributeTitles.tempo:
              selectedTempo = `${attributeName} `;
              break;
            case Attribute.AttributeTitles.stanceGrip:
              selectedStanceGrip = `${attributeName} `;
              break;
            case Attribute.AttributeTitles.loadAccomodation:
              selectedLoadAccomodation += `${attributeName} `;
              break;
            case Attribute.AttributeTitles.accessories:
              selectedAccessories += `${attributeName} `;
              break;
            default:
              selectedOther += `${attributeName} `;
          }
        });

      return `${selectedEquipment}${selectedTempo}${selectedROM}${selectedStanceGrip}${selectedLoadAccomodation}${selectedAccessories}${selectedOther}${t(
        this.originalName,
      )}`;
    }
  }

  setIsSelected(value) {
    this.isSelected = value;
  }

  get hasSeriesWithData() {
    return this.series.some(
      (serie) => serie.weight > 0 || serie.rpe > 0 || serie.reps > 0,
    );
  }

  get hasSeriesWithAnyData() {
    const hasValue = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed === "") return false;
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric) && numeric === 0) return false;
        return true;
      }
      if (typeof value === "number") {
        return Number.isFinite(value) && value !== 0;
      }
      return false;
    };

    return this.series.some((serie) => {
      if (!serie) return false;
      if (serie.hasVideo === true || serie.requireVideo === true) return true;
      if (typeof serie.relevance === "number" && serie.relevance !== 3) {
        return true;
      }
      return (
        hasValue(serie.targetWeight) ||
        hasValue(serie.targetReps) ||
        hasValue(serie.targetRpe) ||
        hasValue(serie.weight) ||
        hasValue(serie.reps) ||
        hasValue(serie.rpe)
      );
    });
  }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
  // #endregion
}
export default Exercise;
